import { Divider, Grid, styled, Typography } from '@mui/material';
import type { Path } from 'react-hook-form';
import {
  NumberInputWidget,
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { ShelfStyleEnum } from 'src/interfaces';
import { lang } from 'src/utils';

import { InfoBox } from '../../../modules/InfoBox';
import { useThemeOptions } from '../../FormSelector.hook';
import { showSettingUpdatedInfoBox } from './ComponentSettingAccordion.helper';
import type { ComponentSettingAccordionProps } from './ComponentSettingAccordion.interface';
import { GroupBadgeController } from './modules/groupBadge/GroupBadgeController';
import {
  columnStyleOptions,
  lineupStyleOrderOptions,
  lineupStylePatternOptions,
  lineupStyleTypeOptions,
  textAlignmentOptions,
} from './modules/helper';
import { ImageBadgeController } from './modules/imageBadge/ImageBadgeController';
import { ComboStyleProductBadge } from './modules/productBadge/ProductBadgeControls';

const BadgeContainer = styled(Grid)`
  display: flex;
  gap: 20px;
  flex-direction: column;
`;

export const ComponentSettingAccordion = (
  props: ComponentSettingAccordionProps,
) => {
  const showLineupStylePattern =
    props.config?.lineupStyle?.type === ShelfStyleEnum.STAGGERED;
  const showInfoBox = showSettingUpdatedInfoBox(props.config?.productList);

  const handleChange =
    (field: Path<ComponentSettingAccordionProps['config']>) => (value: any) =>
      props.handleChange(field, value);

  return (
    <Grid container spacing={2} sx={{ padding: '0 26px 26px' }}>
      {showInfoBox && (
        <Grid item xs={12}>
          <InfoBox>{lang.componentSettingInfo}</InfoBox>
        </Grid>
      )}

      {props.options?.showFlavorCycling && (
        <Grid item xs={12}>
          <SwitchButtonWidget
            label={lang.displayFlavorCycling}
            value={props.config?.isFlavourCycling}
            onChange={handleChange('isFlavourCycling')}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <SwitchButtonWidget
          label={lang.showProductDisclaimers}
          value={props.config?.showComponentProductDisclaimer}
          onChange={handleChange('showComponentProductDisclaimer')}
        />
      </Grid>

      <Grid item xs={12}>
        <SelectDropdownWidget
          label={lang.selectTheme}
          value={props.currentTheme?.id}
          selectOptions={useThemeOptions()}
          onChange={props.handleThemeChange}
        />

        <InfoBox>{lang.selectThemeInfo}</InfoBox>
      </Grid>

      {props.options?.showColumns && (
        <Grid item xs={6}>
          <NumberInputWidget
            label={lang.columns}
            value={props.config?.lineupColumns}
            onChange={handleChange('lineupColumns')}
            options={{
              min: 1,
            }}
          />
        </Grid>
      )}

      {props.options?.showColumnStyle && (
        <Grid item xs>
          <SelectDropdownWidget
            label={lang.columnStyle}
            value={props.config?.columnStyle}
            selectOptions={columnStyleOptions}
            onChange={handleChange('columnStyle')}
          />
        </Grid>
      )}

      {props.options?.showLVGController && (
        <>
          <Grid item xs={12}>
            <NumberInputWidget
              label={lang.listingHeight}
              value={props.config?.listingHeight}
              onChange={handleChange('listingHeight')}
              options={{
                min: 1,
                max: 12,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <SwitchButtonWidget
              label={lang.displayAllVisuals}
              value={props.config?.showAllVisuals}
              onChange={handleChange('showAllVisuals')}
            />
          </Grid>
        </>
      )}

      {props.options?.showSSController && (
        <>
          <Grid item xs={12}>
            <SelectDropdownWidget
              label={lang.lineupStyle}
              value={props.config?.lineupStyle?.type}
              selectOptions={lineupStyleTypeOptions}
              onChange={handleChange('lineupStyle.type')}
            />
          </Grid>

          {showLineupStylePattern && (
            <Grid item xs={12}>
              <SelectDropdownWidget
                label={lang.pattern}
                value={props.config?.lineupStyle?.pattern}
                selectOptions={lineupStylePatternOptions}
                onChange={handleChange('lineupStyle.pattern')}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <SelectDropdownWidget
              label={lang.order}
              value={props.config?.lineupStyle?.order}
              selectOptions={lineupStyleOrderOptions}
              onChange={handleChange('lineupStyle.order')}
            />
          </Grid>

          <Grid item xs={12}>
            <SelectDropdownWidget
              label={lang.alignment}
              value={props.config?.lineupStyle?.textAlignment}
              selectOptions={textAlignmentOptions}
              onChange={handleChange('lineupStyle.textAlignment')}
            />
          </Grid>
        </>
      )}

      {props.options?.showBadgesController && (
        <>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <BadgeContainer item xs={12}>
            <Typography fontWeight={400}>{lang.comboOrderNumber}</Typography>

            <GroupBadgeController
              value={props.config?.productList?.config?.badges?.groupBadge}
              currentTheme={props.currentTheme}
              handleChange={handleChange(
                'productList.config.badges.groupBadge',
              )}
            />
          </BadgeContainer>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <BadgeContainer item xs={12}>
            <Typography fontWeight={400}>{lang.comboBadgeAndBar}</Typography>

            <ComboStyleProductBadge
              value={props.config?.productList?.config?.badges?.itemBadge}
              currentTheme={props.currentTheme}
              onChange={handleChange('productList.config.badges.itemBadge')}
            />
          </BadgeContainer>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <BadgeContainer item xs={12}>
            <Typography fontWeight={400}>{lang.imageBadge}</Typography>

            <ImageBadgeController
              value={props.config?.productList?.config?.badges?.imageBadge}
              currentTheme={props.currentTheme}
              handleChange={handleChange(
                'productList.config.badges.imageBadge',
              )}
            />
          </BadgeContainer>
        </>
      )}
    </Grid>
  );
};
